/**
 * A Desktop container's items: its sub-folders (from the workspace's folder
 * list), its files (the same `useVaultDocumentsList` family Browse and Search
 * read, so a move made anywhere refreshes here), and on the desktop the
 * Trash. Files load a page at a time — on the desktop sorted by name (the
 * order they auto-arrange in, so a new page lands after what is on screen),
 * in a folder window by the folder's view (the server sorts; paging keeps
 * the order).
 */
import { useMemo } from 'react';
import type { VaultDocumentFolderDto } from '@/lib/api';
import { useVaultDocumentsList } from '../../hooks/useVaultDocuments';
import { containerItems, folderIdOfContainer, type DesktopItem } from './desktop-model';
import { ROOT_CONTAINER, type FolderView } from '../../lib/desktop-layout';
import { listSortFor } from '../../lib/folder-view';

export interface ContainerItems {
  items: DesktopItem[];
  loading: boolean;
  /** Files loaded so far vs how many there are. */
  loadedFiles: number;
  totalFiles: number;
  hasMore: boolean;
  loadingMore: boolean;
  loadMore: () => void;
}

export function useContainerItems(input: {
  holderId: string;
  container: string;
  folders: readonly VaultDocumentFolderDto[];
  trashFull: boolean;
  /** Draw the Trash icon here (the Windows desktop; a Mac keeps it in the Dock). */
  includeTrash?: boolean;
  /** A folder window's view (sorted, flowing); absent = the desktop itself. */
  view?: FolderView;
}): ContainerItems {
  const { holderId, container, folders, trashFull, includeTrash = true, view } = input;
  const order = view ? listSortFor(view) : { sort: 'name' as const, direction: 'asc' as const };
  const list = useVaultDocumentsList(holderId, {
    folderId: folderIdOfContainer(container),
    sort: order.sort,
    direction: order.direction,
  });
  const documents = list.data;
  const items = useMemo(
    () =>
      containerItems({
        container,
        folders,
        documents: documents ?? [],
        // The Trash icon lives on the desktop itself, not in a window showing
        // Documents.
        trashFull: container === ROOT_CONTAINER && !view && includeTrash ? trashFull : undefined,
        view,
      }),
    [container, folders, documents, trashFull, includeTrash, view],
  );
  const loadedFiles = documents?.length ?? 0;
  return {
    items,
    loading: list.isLoading,
    loadedFiles,
    totalFiles: list.total,
    hasMore: !!list.hasNextPage,
    loadingMore: list.isFetchingNextPage,
    loadMore: () => void list.fetchNextPage(),
  };
}
